<template>
    <div class="tile">
        <div class="tile-content">
            <div class="grid-content">
                <div class="grid-item grid-item-half grid-item-break-half">
                    <v-select
                        label="Reporting Currency"
                        option-label="label"
                        option-key="value"
                        v-model="reportingCurrency"
                        :options="currenciesList"
                        @input="updatePreferences"
                        help-text="All your reports will be presented in the selected currency."/>
                </div>
            </div>
        </div>
        <div class="tile-content">
            <div class="grid-content">
                <div class="grid-item grid-item-full margin-bottom-m">
                    <p class="stack" :class="{'stack-m': shouldRenderTimezoneNotification}">Your time zone is currently
                        set to: <strong>{{timezone}}</strong>.</p>
                    <div v-if="shouldRenderTimezoneNotification">
                        <div class="message message-warning">
                            <div class="message-content">
                                <p>Your browser time zone currently appears to be: <strong>{{browserTimezone}}</strong>.
                                </p>
                                <p class="stack-m">The application uses the time zone <strong>{{timezone}}</strong> based on profile
                                    settings.</p>
                                <div class="stack-m">
                                    <p>If this is your new <strong>permanent time zone</strong>, please update your profile settings.</p>
                                    <p>If you are <strong>temporarily in this time zone</strong>, please keep in mind that Rebilly shows dates and time in your profile time zone and not the time zone you are currently in.</p>
                                </div>
                                <div>
                                    <span class="inline-s">
                                        <r-button
                                            class="stack-s"
                                            @click="updateProfileDiffTimezone">Update profile time zone to
                                        <b>{{browserTimezone}}</b></r-button>
                                    </span>
                                    <r-button
                                        v-if="!isMutedTimezoneDiffNotification"
                                        @click="hideProfileTimezoneDiffNotification">Hide time zone warning
                                    </r-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="grid-item grid-item-full margin-bottom-m">
                    <r-button class="stack-s" @click="detectTimeZone">Detect my time zone</r-button>
                    <p>Let Rebilly select your time zone as precisely as possible using your computer's location.</p>
                </div>
                <div class="grid-item grid-item-full">
                    <v-select
                        label="Select Time Zone"
                        option-label="label"
                        option-key="name"
                        v-model="timezone"
                        :options="timezoneOptions"
                        @input="updatePreferences"
                        :async-find="customZoneSearch"
                        :async-get-init-value="getInitialZone"
                        :async-config="zoneSearchConfig"
                        help-text="Manually select your local time zone to view
                                date and time information in a specific location."/>
                </div>
            </div>
        </div>
        <div class="tile-content">
            <p class="stack-m">You can reset your local preferences to bring back the interface to
                its normal state
                after you applied modifications. This will restore all grids to their default column
                selection and
                order.</p>
            <r-button type="default" @click="showResetPreferencesDialog"
                      :disabled="isProfileUpdating">Reset Preferences
            </r-button>
        </div>
    </div>
</template>

<script>
    import {mapGetters, mapState} from 'vuex';

    import {vSelect} from '@/components/form';
    import {createDialog, vConfirm} from '@/components/modal/dialogs';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import {CURRENCIES_LIST} from '@/data/currencies';
    import timezones from '@/data/timezones';
    import timezoneOptions from '@/data/timezones/processed.json';
    import isBlank from '@/common/helpers/is-blank';
    import findTimezone from '@/modules/profile/helpers/find-timezone';

    export default {
        name: 'currency-timezones',
        mixins: [
            FormLoaderMixin,
        ],
        components: {
            vSelect,
        },
        props: {
            profile: {
                type: Object,
                required: true,
            },
            isProfileUpdating: Boolean,
        },
        data() {
            return {
                timezone: this.profile.timezone,
                reportingCurrency: this.profile.reportingCurrency,
                timezoneOptions: [...timezoneOptions],
                zoneSearchConfig: {limit: 1000},
            };
        },
        computed: {
            ...mapState({
                browserTimezone: state => state.app.browserTimezone,
            }),
            ...mapGetters({
                uiPreferences: 'user/uiPreferences',
                hasProfileTimezoneDiff: 'app/hasProfileTimezoneDiff',
            }),
            currenciesList() {
                return CURRENCIES_LIST.filter(currency => this.profile.availableCurrencies.includes(currency.value));
            },
            shouldRenderTimezoneNotification() {
                return this.hasProfileTimezoneDiff;
            },
            isMutedTimezoneDiffNotification() {
                return this.uiPreferences.notifications.mutedTimezoneNameDiff === this.browserTimezone;
            },
        },
        methods: {
            async showResetPreferencesDialog() {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: 'Reset Preferences Dialog',
                    content: 'Are you sure you want to reset your preferences?',
                    confirmLabel: 'Reset',
                    cancelLabel: 'Cancel',
                };
                if (await confirmDialog(dialogParams)) {
                    this.resetPreferences();
                }
            },
            async resetPreferences() {
                const reset = {
                    preferences: {
                        timezone: {
                            ui: await this.guessTimezone(),
                        },
                    },
                    totpRequired: this.profile.totpRequired,
                };
                this.$emit('input', reset);
            },
            async updatePreferences(timezone = null, mutedTimezoneNotification = null) {
                const {preferences} = this.profile;
                // todo probably use safe method this.uiPreferences.notifications
                const notificationPreferences = preferences.notifications || {};

                const mutedTimezoneNameDiff = mutedTimezoneNotification
                    ? `${this.$tz()
                        .getUTC()
                        .format('YYYY-MM-DD')}|${this.browserTimezone}`
                    : notificationPreferences.mutedTimezoneNameDiff;

                const data = {
                    reportingCurrency: this.reportingCurrency,
                    totpRequired: this.profile.totpRequired,
                    preferences: {
                        ...preferences,
                        notifications: {
                            ...notificationPreferences,
                            // clear the muted value when the timezone was updated
                            mutedTimezoneNameDiff: timezone ? '' : mutedTimezoneNameDiff,
                        },
                        timezone: {
                            ui: timezone || this.timezone,
                        },
                    },
                };
                this.$emit('input', data);
            },
            async guessTimezone() {
                const {fields} = await this.$api(({location}) => location.query());
                return fields.timezone || timezones.default;
            },
            async confirmUpdateTimezone(timezone) {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: 'Your Time Zone',
                    confirmLabel: 'Apply',
                    content: `Rebilly detected ${timezone} as your time zone. Do you wish to apply this value?`,
                };
                if (await confirmDialog(dialogParams)) {
                    await this.updatePreferences(timezone);
                }
            },
            async detectTimeZone() {
                const timezone = await this.guessTimezone();
                await this.confirmUpdateTimezone(timezone);
            },
            async updateProfileDiffTimezone() {
                await this.confirmUpdateTimezone(this.browserTimezone);
            },
            async hideProfileTimezoneDiffNotification() {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: `Hide Time Zone Warning`,
                    confirmLabel: 'Apply',
                    content: `Rebilly has detected ${this.browserTimezone} as your local timezone, this value differs from the timezone set in your profile settings (${this.timezone}). Do you wish to hide this time zone warning for the next 31 days?`,
                };
                if (await confirmDialog(dialogParams)) {
                    await this.updatePreferences(null, true);
                }
            },
            async customZoneSearch(search = '') {
                if (isBlank(search)) {
                    return this.timezoneOptions;
                }
                return findTimezone(search, this.timezoneOptions);
            },
            async getInitialZone() {
                return [this.timezoneOptions.find(({name}) => this.timezone === name)];
            },
        },
        watch: {
            profile() {
                this.timezone = this.profile.timezone;
                this.reportingCurrency = this.profile.reportingCurrency;
            },
        },
    };
</script>
